import { useCallback, useEffect, useMemo, useState } from 'react';
import { lightTheme, darkTheme } from '@styles/theme';
import { STORAGE_KEYS } from '@constants/storageKeys';
import {
  isThemeMode,
  readThemeMode,
  resolveTheme,
  THEME_MODES
} from '@models/theme/themeModel';

function getInitialThemeMode() {
  try {
    return readThemeMode();
  } catch (error) {
    console.error('Failed to read theme preference:', error);
    return THEME_MODES.SYSTEM;
  }
}

export function useThemeController() {
  const [themeMode, setThemeMode] = useState(getInitialThemeMode);
  const [systemPrefersDark, setSystemPrefersDark] = useState(
    () => window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event) => setSystemPrefersDark(event.matches);

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const theme = useMemo(
    () => resolveTheme(themeMode, systemPrefersDark, {
      light: lightTheme,
      dark: darkTheme
    }),
    [themeMode, systemPrefersDark]
  );

  const changeThemeMode = useCallback((newMode) => {
    if (!isThemeMode(newMode)) {
      console.error('Invalid theme mode:', newMode);
      return;
    }

    setThemeMode(newMode);

    try {
      localStorage.setItem(STORAGE_KEYS.THEME_PREFERENCE, newMode);
    } catch (error) {
      console.error('Failed to save theme preference:', error);
    }
  }, []);

  return {
    themeMode,
    setThemeMode: changeThemeMode,
    currentTheme: theme.palette.mode,
    systemPrefersDark,
    theme
  };
}
